// Canonical enumerations shared by API, worker, Telegram and Web.
// The database stores these exact string values.

export const TASK_STATUSES = [
  'INBOX',
  'PLANNED',
  'IN_PROGRESS',
  'WAITING',
  'COMPLETED',
  'CANCELLED',
  'ARCHIVED',
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const ACTIVE_STATUSES: readonly TaskStatus[] = ['INBOX', 'PLANNED', 'IN_PROGRESS', 'WAITING'];
export const CLOSED_STATUSES: readonly TaskStatus[] = ['COMPLETED', 'CANCELLED', 'ARCHIVED'];

export const PRIORITIES = ['URGENT', 'HIGH', 'MEDIUM', 'LOW'] as const;
export type Priority = (typeof PRIORITIES)[number];
export const PRIORITY_RANK: Record<Priority, number> = { URGENT: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
export const PRIORITY_META: Record<Priority, { emoji: string; label: string }> = {
  URGENT: { emoji: '🔴', label: 'Urgent' },
  HIGH: { emoji: '🟠', label: 'High' },
  MEDIUM: { emoji: '🟡', label: 'Medium' },
  LOW: { emoji: '🟢', label: 'Low' },
};

export const TASK_TYPES = ['FIXED_DATE', 'RECURRING', 'ANYTIME', 'FOLLOW_UP', 'QUICK', 'ONGOING'] as const;
export type TaskType = (typeof TASK_TYPES)[number];
export const TASK_TYPE_META: Record<TaskType, { emoji: string; label: string; hint: string }> = {
  FIXED_DATE: { emoji: '📅', label: 'Fixed date', hint: 'Has a due date (and optional time)' },
  RECURRING: { emoji: '🔁', label: 'Recurring', hint: 'Repeats on a schedule' },
  ANYTIME: { emoji: '📥', label: 'Anytime', hint: 'No date — do it whenever' },
  FOLLOW_UP: { emoji: '⏳', label: 'Follow-up', hint: 'Check back on something later' },
  QUICK: { emoji: '⚡', label: 'Quick task', hint: 'Title only, due today' },
  ONGOING: { emoji: '📌', label: 'Ongoing', hint: 'Long-running, tracked by next action' },
};

export const STATUS_META: Record<TaskStatus, { label: string }> = {
  INBOX: { label: 'Inbox' },
  PLANNED: { label: 'Planned' },
  IN_PROGRESS: { label: 'In progress' },
  WAITING: { label: 'Waiting' },
  COMPLETED: { label: 'Completed' },
  CANCELLED: { label: 'Cancelled' },
  ARCHIVED: { label: 'Archived' },
};

export const VISIBILITIES = ['TEAM', 'PERSONAL', 'PRIVATE'] as const;
export type Visibility = (typeof VISIBILITIES)[number];

export const WAITING_FOR = ['BANK', 'CLIENT', 'VENDOR', 'EMPLOYEE', 'GOVERNMENT', 'OTHER'] as const;
export type WaitingFor = (typeof WAITING_FOR)[number];

export const ROLES = ['ADMIN', 'MANAGER', 'STAFF'] as const;
export type Role = (typeof ROLES)[number];

export const SOURCES = ['WEB', 'TELEGRAM', 'SYSTEM', 'API'] as const;
export type Source = (typeof SOURCES)[number];

export const REMINDER_MODES = ['ONCE', 'REPEAT', 'ESCALATE'] as const;
export type ReminderMode = (typeof REMINDER_MODES)[number];

export const REMINDER_INTENSITIES = ['LOW', 'NORMAL', 'HIGH', 'CRITICAL'] as const;
export type ReminderIntensity = (typeof REMINDER_INTENSITIES)[number];

/** How a reminder's fire time is derived. */
export const REMINDER_KINDS = ['BEFORE_DUE', 'AT_DUE', 'AFTER_DUE', 'ABSOLUTE'] as const;
export type ReminderKind = (typeof REMINDER_KINDS)[number];

export const REMINDER_STATUSES = ['PENDING', 'SENT', 'CANCELLED', 'SUPPRESSED'] as const;
export type ReminderStatus = (typeof REMINDER_STATUSES)[number];

export const RECURRENCE_FREQS = [
  'DAILY',
  'WEEKLY',
  'MONTHLY',
  'YEARLY',
  'INTERVAL_DAYS',
  'WEEKDAYS',
  'WORKING_DAYS',
] as const;
export type RecurrenceFreq = (typeof RECURRENCE_FREQS)[number];

export const HOLIDAY_POLICIES = ['NONE', 'NEXT_WORKING_DAY', 'PREVIOUS_WORKING_DAY', 'SKIP'] as const;
export type HolidayPolicy = (typeof HOLIDAY_POLICIES)[number];

export const APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'DENIED', 'DETAILS_REQUESTED', 'WITHDRAWN'] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number];

export const APPROVAL_ACTIONS = ['CREATE', 'APPROVE', 'DENY', 'ASK_DETAILS', 'PROVIDE_DETAILS', 'WITHDRAW'] as const;
export type ApprovalAction = (typeof APPROVAL_ACTIONS)[number];

export const TASK_EVENT_TYPES = [
  'TASK_CREATED',
  'TASK_UPDATED',
  'TASK_COMPLETED',
  'TASK_REOPENED',
  'TASK_CANCELLED',
  'TASK_ARCHIVED',
  'TASK_UNARCHIVED',
  'TASK_SNOOZED',
  'TASK_RESCHEDULED',
  'TASK_STATUS_CHANGED',
  'TASK_ASSIGNED',
  'TASK_REASSIGNED',
  'PRIORITY_CHANGED',
  'REMINDER_CREATED',
  'REMINDER_REMOVED',
  'REMINDER_SENT',
  'REMINDER_SUPPRESSED',
  'FOLLOWUP_CREATED',
  'FOLLOWUP_COMPLETED',
  'RECURRENCE_OCCURRENCE_CREATED',
  'PRESET_APPLIED',
  'ATTACHMENT_ADDED',
  'ATTACHMENT_REMOVED',
  'NOTE_ADDED',
  'PRIVATE_TASK_ACCESSED',
  'CONVERTED_FROM_INBOX',
] as const;
export type TaskEventType = (typeof TASK_EVENT_TYPES)[number];

export const AUDIT_ACTIONS = [
  'LOGIN',
  'LOGIN_FAILED',
  'LOGOUT',
  'PASSWORD_CHANGED',
  'PERMISSION_CHANGED',
  'ROLE_CHANGED',
  'STAFF_CREATED',
  'STAFF_UPDATED',
  'STAFF_DISABLED',
  'STAFF_ENABLED',
  'ADMIN_OVERRIDE',
  'PRIVATE_TASK_ACCESSED',
  'TASK_CREATED',
  'TASK_UPDATED',
  'TASK_COMPLETED',
  'TASK_CANCELLED',
  'TASK_ARCHIVED',
  'TASK_ASSIGNED',
  'TASK_REASSIGNED',
  'TASK_BULK_ACTION',
  'APPROVAL_CREATED',
  'APPROVAL_APPROVED',
  'APPROVAL_DENIED',
  'APPROVAL_DETAILS_REQUESTED',
  'APPROVAL_DETAILS_PROVIDED',
  'APPROVAL_WITHDRAWN',
  'APPROVAL_UNAUTHORIZED_ATTEMPT',
  'SETTINGS_CHANGED',
  'HOLIDAY_CREATED',
  'HOLIDAY_UPDATED',
  'HOLIDAY_DELETED',
  'PRESET_CREATED',
  'PRESET_UPDATED',
  'PRESET_DELETED',
  'TELEGRAM_LINKED',
  'TELEGRAM_UNLINKED',
  'TELEGRAM_CONFIG_CHANGED',
  'BACKUP_CREATED',
  'BACKUP_RESTORED',
  'BACKUP_DELETED',
  'DATA_EXPORTED',
  'DATA_IMPORTED',
  'DATA_CLEARED',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const PERMISSIONS = [
  'task.view_team', // see TEAM tasks of others
  'task.create',
  'task.edit_any', // edit tasks not created by / assigned to me
  'task.assign', // assign tasks to other people
  'task.reassign',
  'task.complete_any',
  'task.cancel_any',
  'task.archive_any',
  'task.access_private', // admin override on others' private tasks (always logged)
  'task.bulk',
  'preset.manage_global',
  'holiday.manage',
  'settings.manage',
  'users.manage',
  'approval.request',
  'approval.approve',
  'approval.view_all',
  'audit.view',
  'telegram.manage',
  'backup.manage',
  'data.export',
  'data.import',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const PERMISSION_LABELS: Record<Permission, string> = {
  'task.view_team': 'Can view team tasks',
  'task.create': 'Can create tasks',
  'task.edit_any': 'Can edit any task',
  'task.assign': 'Can assign tasks',
  'task.reassign': 'Can reassign tasks',
  'task.complete_any': 'Can complete any task',
  'task.cancel_any': 'Can cancel any task',
  'task.archive_any': 'Can archive any task',
  'task.access_private': 'Can access private tasks (override, logged)',
  'task.bulk': 'Can use bulk actions',
  'preset.manage_global': 'Can manage global presets',
  'holiday.manage': 'Can manage holidays & working days',
  'settings.manage': 'Can manage system settings',
  'users.manage': 'Can manage users & roles',
  'approval.request': 'Can request approvals',
  'approval.approve': 'Can approve / deny',
  'approval.view_all': 'Can view all approvals',
  'audit.view': 'Can view audit log',
  'telegram.manage': 'Can manage Telegram',
  'backup.manage': 'Can manage backups',
  'data.export': 'Can export data',
  'data.import': 'Can import data',
};

/** Default permission sets per role. Admins may change role permissions and per-user overrides. */
export const DEFAULT_ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  ADMIN: PERMISSIONS,
  MANAGER: [
    'task.view_team',
    'task.create',
    'task.edit_any',
    'task.assign',
    'task.reassign',
    'task.complete_any',
    'task.cancel_any',
    'task.archive_any',
    'task.bulk',
    'approval.request',
    'approval.view_all',
    'audit.view',
    'data.export',
  ],
  STAFF: ['task.view_team', 'task.create', 'task.bulk', 'approval.request'],
};

export const THEMES = ['LIGHT', 'DARK', 'SYSTEM'] as const;
export type Theme = (typeof THEMES)[number];

export const TELEGRAM_BOTS = ['TASK', 'APPROVAL'] as const;
export type TelegramBotKind = (typeof TELEGRAM_BOTS)[number];

export const SORT_FIELDS = ['priority', 'due', 'created', 'updated', 'assignee', 'title'] as const;
export type SortField = (typeof SORT_FIELDS)[number];

export const TASK_VIEWS = ['TILES', 'LIST', 'CALENDAR', 'TIMELINE'] as const;
export type TaskViewMode = (typeof TASK_VIEWS)[number];
